import { useState } from 'react';
import { useIntl } from 'react-intl';
import AIEduDialog from '../../components/Dialog/AIEduDialog';
import AIEduInputText from '../../components/Input/AIEduInputText';
import AIEduButton from '../../components/Button/AIEduButton';
import { fetchFromPort } from '../../utils/NFCUtils';

const NFCReaderDialog = ({ visible, onHide, onConfirm }) => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const [cardNumber, setCardNumber] = useState('');
    const [isScanning, setIsScanning] = useState(false);

    const handleNFCScan = async () => {
        // Prevent multiple simultaneous scans
        if (isScanning) return;
        setIsScanning(true);
        try {
            // Scan for NFC card with the same active port
            let scanData;
            try {
                const { response: scanResponse } = await fetchFromPort('/scan');
                if (!scanResponse.ok) {
                    // Handle specific error cases
                    if (scanResponse.status === 504) {
                        throw new Error(
                            f({ id: 'AI_EDU_NFC_CARD_NOT_DETECTED' })
                        );
                    }
                    throw new Error(
                        f(
                            { id: 'AI_EDU_NFC_SCAN_FAILED' },
                            { status: scanResponse.status }
                        )
                    );
                }
                scanData = await scanResponse.json();
            } catch (error) {
                // Handle network errors specifically
                if (
                    error instanceof TypeError &&
                    error.message.includes('fetch')
                ) {
                    throw new Error(f({ id: 'AI_EDU_NFC_CONNECTION_LOST' }));
                }
                throw error;
            }

            // Validate that we received a UID
            if (!scanData.uid) {
                throw new Error(f({ id: 'AI_EDU_NFC_INVALID_RESPONSE' }));
            }

            // Update card number and trigger auto-close
            setCardNumber(scanData.uid);
        } catch (error) {
            console.error('NFC scan error:', error);
            alert(error.message || f({ id: 'AI_EDU_NFC_SCAN_ERROR' }));
        } finally {
            setIsScanning(false);
        }
    };

    const handleHide = () => {
        setCardNumber('');
        onHide();
    };

    const content = (
        <div className='nfc_reader_container'>
            <div style={{ marginBottom: '20px' }}>
                <ol className='nfc_reader_list'>
                    <li>
                        {f({ id: 'AI_EDU_NFC_DOWNLOAD_AGENT' })}{' '}
                        <a href='https://dev.samsungeduai.com/nfc-connector/NFCEnrollmentAgent-Setup.exe'>
                            here.
                        </a>
                    </li>
                    <li>{f({ id: 'AI_EDU_NFC_CONNECT_USB' })}</li>
                    <li>{f({ id: 'AI_EDU_NFC_INSTALL_DRIVER' })}</li>
                    <li>{f({ id: 'AI_EDU_NFC_TEST_METHOD' })}</li>
                </ol>
            </div>

            <div style={{ marginTop: '30px' }}>
                <AIEduInputText
                    title={f({ id: 'AI_EDU_NFC_CARD_NUMBER' })}
                    value={cardNumber}
                    placeholder={f({
                        id: 'AI_EDU_NFC_CARD_NUMBER_PLACEHOLDER',
                    })}
                    readOnly={true}
                    style={{ width: '100%' }}
                />
            </div>
        </div>
    );

    const handleConfirm = async () => {
        // Pass the card number to the parent component
        onConfirm(cardNumber);
        onHide();
    };

    const footer = (
        <div className='flex justify-content-end'>
            <AIEduButton
                label={f({ id: 'AI_EDU_CLOSE' })}
                onClick={handleHide}
                className='btnSecondary'
            />
            <AIEduButton
                label={f({ id: 'AI_EDU_NFC_SCAN' })}
                onClick={handleNFCScan}
                className='btnPrimary'
            />
            <AIEduButton
                label={f({ id: 'AI_EDU_CONFIRM' })}
                onClick={handleConfirm}
                disabled={isScanning}
                className='btnPrimary'
            />
        </div>
    );

    return (
        <AIEduDialog
            visible={visible}
            onHide={handleHide}
            footer={footer}
            content={content}
            style={{ width: '500px' }}
            breakpoints={{ '960px': '75vw', '640px': '90vw' }}
        />
    );
};

export default NFCReaderDialog;
